/**
 * 导出面板的「期次」：候选由服务端 `GET /orders/export/periods` 下发的**有账月份**派生，
 * 不再按「今天往前推 12/8/5 档」自己造期。
 *
 * 为什么要换：原先的候选和用户名下有没有账毫无关系——账是从 2026 年开始的用户，
 * 在「2022 年」那一档点导出会拿回一张空表，纸面上看不出缺。改成数据驱动之后，
 * 下拉里出现的每一期都真有单，还能顺带报出单量。
 *
 * 派生只写在这一处，页面只管选哪种档位、选第几期。日期一律走**本地日历日**
 * （见 `utils/dateUtils.ts` 开头：浏览器所在即业务时区，本地日就是后端业务日），
 * 这里禁止出现 `toISOString()`——那是 UTC 日，东八区月初凌晨导出会得到上一个月的账。
 *
 * 期次的边界是**活动日期**（`order_start_date`），与列表页那两个日期框、以及服务端分桶同一个列：
 * 「按 2026 年 10 月导出」= 档期落在这个月的单，不是「这个月创建的单」。
 * 后端目前没有任何按创建时间区间筛选的入参，要做就得加 DTO 键，属另一件事。
 */
import { formatLocalDate } from '../../../utils/dateUtils';

export type PeriodKind = 'month' | 'quarter' | 'year' | 'custom';

export interface ExportPeriod {
  /** 面板与文件名共用的一句话，如「2026年10月」「2026年三季度」「2026-01-01至2026-03-31」 */
  label: string;
  /** 空串表示这一头不限；自定义档允许两头都空（= 不限日期） */
  dateFrom: string;
  dateTo: string;
  /** 这一期在我台账里的单数；自定义档不由分桶派生，故没有这个数 */
  orderCount?: number;
}

/** 服务端下发的一个月桶：年月是值，单量是这个月份内参与导出的订单数 */
export interface ExportMonthBucket {
  year: number;
  month: number;
  orderCount: number;
}

/** 档位顺序即面板顺序：默认按月（见 DEFAULT_PERIOD_KIND），自定义作兜底档排最后 */
export const PERIOD_KINDS: ReadonlyArray<{ value: PeriodKind; label: string }> = [
  { value: 'month', label: '按月' },
  { value: 'quarter', label: '按季' },
  { value: 'year', label: '按年' },
  { value: 'custom', label: '自定义' },
];

export const DEFAULT_PERIOD_KIND: PeriodKind = 'month';

const QUARTER_CN = ['一', '二', '三', '四'];

const pad = (n: number) => String(n).padStart(2, '0');

/** 某年某月的闭区间。`new Date(year, month, 0)` 是本月最后一天，闰年二月由 Date 自己算。 */
function monthRange(year: number, month: number): { dateFrom: string; dateTo: string } {
  return {
    dateFrom: formatLocalDate(new Date(year, month - 1, 1)),
    dateTo: formatLocalDate(new Date(year, month, 0)),
  };
}

/** 自定义区间在面板提示与文件名里共用的那一句话；两头都空 = 不限日期，出空串（文件名不带后缀） */
export function customRangeLabel(dateFrom: string, dateTo: string): string {
  if (dateFrom && dateTo) return `${dateFrom}至${dateTo}`;
  if (dateFrom) return `${dateFrom}起`;
  if (dateTo) return `至${dateTo}`;
  return '';
}

/**
 * 区间填反了（起 > 止）。`YYYY-MM-DD` 定长，字典序即时间序，所以直接比串——
 * 与后端 `utils/business-date.ts` 第 1 类纯日期的比较规则同一条，不再 `new Date()` 一次。
 */
export function isRangeReversed(dateFrom: string, dateTo: string): boolean {
  return !!dateFrom && !!dateTo && dateFrom > dateTo;
}

/** 自定义档产出的「期次」：哪怕两头都空也照样返回对象，表示日期维度已由面板接管 */
export function customPeriodOf(dateFrom: string, dateTo: string): ExportPeriod {
  return { label: customRangeLabel(dateFrom, dateTo), dateFrom, dateTo };
}

/**
 * 月度分桶 → 该档位的候选期次，从近到远。
 *
 * 季与年是同一批月桶的**合并**：边界取整个日历季/年（那一季哪怕只有一个月有账，
 * 导出的也是整季另两个月照旧为空），单量按成员月相加。合并而不是让后端再发两份清单——
 * 三份清单就可能三份口径，月桶是唯一事实源。
 */
export function periodsFromBuckets(
  kind: PeriodKind,
  buckets: ReadonlyArray<ExportMonthBucket>,
): ExportPeriod[] {
  if (kind === 'custom') return [];
  const merged = new Map<string, ExportPeriod>();
  for (const bucket of buckets) {
    const { year, month } = bucket;
    let key: string;
    let label: string;
    let range: { dateFrom: string; dateTo: string };
    if (kind === 'year') {
      key = `${year}`;
      label = `${year}年`;
      range = { dateFrom: monthRange(year, 1).dateFrom, dateTo: monthRange(year, 12).dateTo };
    } else if (kind === 'quarter') {
      const q = Math.floor((month - 1) / 3);
      key = `${year}-Q${q}`;
      label = `${year}年${QUARTER_CN[q]}季度`;
      range = { dateFrom: monthRange(year, q * 3 + 1).dateFrom, dateTo: monthRange(year, q * 3 + 3).dateTo };
    } else {
      key = `${year}-${pad(month)}`;
      label = `${year}年${month}月`;
      range = monthRange(year, month);
    }
    const existing = merged.get(key);
    if (existing) {
      existing.orderCount = (existing.orderCount ?? 0) + bucket.orderCount;
    } else {
      merged.set(key, { label, ...range, orderCount: bucket.orderCount });
    }
  }
  return [...merged.values()].sort((a, b) => (a.dateFrom < b.dateFrom ? 1 : -1));
}
